import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { equipment } from '../../lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarDays,
  CircleDot,
  Edit3,
  Filter,
  Package,
  Plus,
  Search,
  ShieldCheck,
  ShoppingBag,
  Tag,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import i18nInstance from '../../i18n';


const t = (key, fallback, options = {}) =>
  i18nInstance.t(key, typeof fallback === 'string' ? { defaultValue: fallback, ...options } : fallback);
const CATEGORY_OPTIONS = ['racket', 'strings', 'shoes', 'clothing', 'balls', 'bag', 'other'];

const CATEGORY_META = {
  racket: {
    labelKey: 'equipment.categories.racket',
    Icon: Package,
    badge: 'bg-lime-100 text-lime-800 border-lime-200',
    accent: 'from-lime-400 to-lime-600',
    dot: 'bg-lime-400',
  },
  strings: {
    labelKey: 'equipment.categories.strings',
    Icon: CircleDot,
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    accent: 'from-cyan-400 to-blue-500',
    dot: 'bg-cyan-400',
  },
  shoes: {
    labelKey: 'equipment.categories.shoes',
    Icon: ShieldCheck,
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    accent: 'from-purple-400 to-violet-600',
    dot: 'bg-purple-400',
  },
  clothing: {
    labelKey: 'equipment.categories.clothing',
    Icon: Tag,
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    accent: 'from-amber-400 to-orange-500',
    dot: 'bg-amber-400',
  },
  balls: {
    labelKey: 'equipment.categories.balls',
    Icon: CircleDot,
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    accent: 'from-yellow-300 to-lime-500',
    dot: 'bg-yellow-300',
  },
  bag: {
    labelKey: 'equipment.categories.bag',
    Icon: ShoppingBag,
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    accent: 'from-emerald-400 to-teal-600',
    dot: 'bg-emerald-400',
  },
  other: {
    labelKey: 'equipment.categories.other',
    Icon: Package,
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    accent: 'from-zinc-400 to-zinc-600',
    dot: 'bg-zinc-400',
  },
};

const CONDITION_META = {
  excellent: {
    labelKey: 'equipment.conditions.excellent',
    level: 16,
    tone: 'bg-lime-100 text-lime-800 border-lime-200',
    meter: 'bg-lime-500',
  },
  good: {
    labelKey: 'equipment.conditions.good',
    level: 34,
    tone: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    meter: 'bg-emerald-500',
  },
  fair: {
    labelKey: 'equipment.conditions.fair',
    level: 66,
    tone: 'bg-amber-100 text-amber-800 border-amber-200',
    meter: 'bg-amber-500',
  },
  poor: {
    labelKey: 'equipment.conditions.poor',
    level: 88,
    tone: 'bg-rose-100 text-rose-800 border-rose-200',
    meter: 'bg-rose-500',
  },
};

const normalizeUsageResponse = (data) => {
  if (Array.isArray(data)) {
    return { logs: data, totalMinutes: null };
  }
  if (!data || typeof data !== 'object') {
    return { logs: [], totalMinutes: null };
  }
  const logs = Array.isArray(data.usage_logs)
    ? data.usage_logs
    : Array.isArray(data.logs)
      ? data.logs
      : [];
  const totalMinutes =
    data.usage_total_minutes ??
    data.total_duration_minutes ??
    data.total_minutes ??
    (typeof data.usage_total_hours === 'number' ? data.usage_total_hours * 60 : null);
  return { logs, totalMinutes };
};

export const EquipmentPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [usageLogs, setUsageLogs] = useState([]);
  const [usageTotalFromApi, setUsageTotalFromApi] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    category: 'racket',
    condition: 'good',
    purchase_date: '',
    notes: '',
    need_to_buy: false,
  });

  const fetchEquipment = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await equipment.getAll();
      setEquipmentList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setLoadError(t('equipment.loadError'));
      toast.error(t('equipment.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  useEffect(() => {
    if (equipmentList.length === 0) {
      setSelectedEquipmentId(null);
      return;
    }
    if (!selectedEquipmentId || !equipmentList.some((item) => item.id === selectedEquipmentId)) {
      setSelectedEquipmentId(equipmentList[0].id);
    }
  }, [equipmentList, selectedEquipmentId]);

  const fetchUsageHistory = useCallback(async (equipmentId) => {
    if (!equipmentId) {
      setUsageLogs([]);
      setUsageTotalFromApi(null);
      setUsageError('');
      return;
    }

    setUsageLoading(true);
    setUsageError('');
    try {
      const response = await equipment.getUsage(equipmentId);
      const { logs, totalMinutes } = normalizeUsageResponse(response.data);
      setUsageLogs(logs);
      setUsageTotalFromApi(totalMinutes);
    } catch (error) {
      setUsageLogs([]);
      setUsageTotalFromApi(null);
      setUsageError(t('equipment.usageLoadError'));
    } finally {
      setUsageLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsageHistory(selectedEquipmentId);
  }, [fetchUsageHistory, selectedEquipmentId]);

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'racket',
      condition: 'good',
      purchase_date: '',
      notes: '',
      need_to_buy: false,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingId(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        athlete_id: user?.id,
        purchase_date: formData.purchase_date ? new Date(formData.purchase_date).toISOString() : null,
      };

      if (editingId) {
        await equipment.update(editingId, data);
        toast.success(t('equipment.updateSuccess'));
      } else {
        await equipment.create(data);
        toast.success(t('equipment.addSuccess'));
      }

      setShowModal(false);
      setEditingId(null);
      resetForm();
      fetchEquipment();
    } catch (error) {
      toast.error(t('equipment.saveError'));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name || '',
      category: item.category || 'racket',
      condition: item.condition || 'good',
      purchase_date: item.purchase_date ? new Date(item.purchase_date).toISOString().split('T')[0] : '',
      notes: item.notes || '',
      need_to_buy: Boolean(item.need_to_buy),
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('equipment.deleteConfirm'))) return;
    try {
      await equipment.delete(id);
      toast.success(t('equipment.deleteSuccess'));
      fetchEquipment();
    } catch (error) {
      toast.error(t('equipment.deleteError'));
    }
  };

  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB';

  const localeDateString = (dateStr) => {
    const parsed = new Date(dateStr);
    return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString(locale);
  };

  const getCategoryMeta = (category) => CATEGORY_META[category] || CATEGORY_META.other;
  const getCategoryLabel = (category) => t(getCategoryMeta(category).labelKey);

  const getConditionMeta = (item) => {
    if (item.need_to_buy) {
      return {
        labelKey: 'equipment.needsAttention',
        level: 94,
        tone: 'bg-rose-100 text-rose-800 border-rose-200',
        meter: 'bg-rose-500',
      };
    }
    return CONDITION_META[item.condition] || {
      labelKey: 'equipment.conditionSet',
      level: 50,
      tone: 'bg-zinc-100 text-zinc-700 border-zinc-200',
      meter: 'bg-zinc-500',
    };
  };

  const categoryCounts = useMemo(() => {
    return equipmentList.reduce((counts, item) => {
      const category = item.category || 'other';
      counts[category] = (counts[category] || 0) + 1;
      return counts;
    }, {});
  }, [equipmentList]);

  const activeCategoryOptions = useMemo(() => {
    const fromData = Object.keys(categoryCounts);
    return CATEGORY_OPTIONS.filter((category) => categoryCounts[category]).concat(
      fromData.filter((category) => !CATEGORY_OPTIONS.includes(category))
    );
  }, [categoryCounts]);

  const attentionItems = useMemo(() => {
    return equipmentList.filter((item) => item.need_to_buy || item.condition === 'poor');
  }, [equipmentList]);

  const newestItem = useMemo(() => {
    return [...equipmentList]
      .filter((item) => item.purchase_date)
      .sort((a, b) => new Date(b.purchase_date) - new Date(a.purchase_date))[0];
  }, [equipmentList]);

  const filteredEquipment = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return equipmentList.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const searchable = [item.name, item.category, item.condition, item.notes].filter(Boolean).join(' ').toLowerCase();
      return matchesCategory && (!query || searchable.includes(query));
    });
  }, [activeCategory, equipmentList, searchTerm]);

  const selectedEquipment = useMemo(
    () => equipmentList.find((item) => item.id === selectedEquipmentId) || null,
    [equipmentList, selectedEquipmentId],
  );

  const usageTotalMinutes = useMemo(() => {
    if (usageTotalFromApi !== null && usageTotalFromApi !== undefined) {
      const total = Number(usageTotalFromApi);
      return Number.isFinite(total) ? total : 0;
    }
    return usageLogs.reduce((total, log) => total + (Number(log.duration_minutes) || 0), 0);
  }, [usageLogs, usageTotalFromApi]);

  const formatUsageDuration = useCallback((minutes) => {
    const totalMinutes = Math.max(0, Number(minutes) || 0);
    if (totalMinutes >= 60) {
      const hours = totalMinutes / 60;
      return `${new Intl.NumberFormat(locale, { maximumFractionDigits: hours % 1 === 0 ? 0 : 1 }).format(hours)} ${t('equipment.hours')}`;
    }
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(totalMinutes)} ${t('equipment.minutes')}`;
  }, [locale, t]);

  const usageSourceLabel = (sourceType) => {
    if (sourceType === 'training') return t('equipment.usedInTraining');
    if (sourceType === 'match') return t('equipment.usedInMatch');
    return sourceType || t('equipment.source');
  };

  const inventorySummary = [
    {
      label: t('equipment.totalItems'),
      value: equipmentList.length,
      detail: t(equipmentList.length === 1 ? 'equipment.trackedItem' : 'equipment.trackedItems'),
      Icon: Package,
      color: 'text-zinc-900',
    },
    {
      label: t('equipment.needsAttention'),
      value: attentionItems.length,
      detail: attentionItems.length ? t('equipment.reviewBeforeNextSession') : t('equipment.allGearReady'),
      Icon: AlertTriangle,
      color: attentionItems.length ? 'text-rose-600' : 'text-emerald-600',
    },
    {
      label: t('equipment.category'),
      value: Object.keys(categoryCounts).length,
      detail: t('equipment.organizedByType'),
      Icon: Filter,
      color: 'text-cyan-700',
    },
    {
      label: t('equipment.newestItem'),
      value: newestItem ? getCategoryLabel(newestItem.category) : '-',
      detail: newestItem ? newestItem.name : t('equipment.addPurchaseDates'),
      Icon: CalendarDays,
      color: 'text-purple-700',
    },
  ];

  const renderEquipmentCard = (item) => {
    const category = getCategoryMeta(item.category);
    const condition = getConditionMeta(item);
    const CategoryIcon = category.Icon;

    return (
      <article
        key={item.id}
        className="group overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-xl hover:shadow-zinc-200/70"
        data-testid={`equipment-card-${item.id}`}
      >
        <div className={`h-1.5 bg-gradient-to-r ${category.accent}`} />
        <div className="p-5">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-zinc-200 bg-zinc-950 text-lime-300">
                <CategoryIcon size={20} />
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-lg font-semibold tracking-tight text-zinc-950">{item.name}</h3>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${category.badge}`}>
                    {t(category.labelKey)}
                  </span>
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${condition.tone}`}>
                    {t(condition.labelKey)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => handleEdit(item)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:border-lime-300 hover:bg-lime-50 hover:text-zinc-950"
                data-testid={`edit-equipment-${item.id}`}
                aria-label={t('equipment.editItemAria', { name: item.name })}
              >
                <Edit3 size={17} />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(item.id)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                data-testid={`delete-equipment-${item.id}`}
                aria-label={t('equipment.deleteItemAria', { name: item.name })}
              >
                <Trash2 size={17} />
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
            <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-zinc-500">
              <span>{t('equipment.wearStatus')}</span>
              <span>{condition.level}%</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-200">
              <div
                className={`h-2 rounded-full ${condition.meter}`}
                style={{ width: `${condition.level}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-zinc-500">{t('equipment.wearStatusBody')}</p>
          </div>

          <div className="mt-4 grid gap-3 text-sm text-zinc-600">
            {item.purchase_date && (
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-zinc-500">
                  <CalendarDays size={15} />
                  {t('equipment.purchaseDate')}
                </span>
                <span className="font-medium text-zinc-900">{localeDateString(item.purchase_date)}</span>
              </div>
            )}
            {item.notes && (
              <p className="rounded-xl border border-zinc-100 bg-white p-3 leading-relaxed text-zinc-600">{item.notes}</p>
            )}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="equipment-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-8 h-56 w-56 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-1/2 h-px w-80 -rotate-12 bg-lime-300" />
            <div className="absolute bottom-16 left-8 h-px w-64 rotate-6 bg-white/40" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('equipment.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('equipment.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('equipment.heroBody')}
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-lime-300 px-5 py-3 text-sm font-semibold text-zinc-950 shadow-lg shadow-lime-950/30 transition hover:bg-lime-200"
              data-testid="add-equipment-button"
            >
              <Plus size={19} />
              {t('equipment.addItem')}
            </button>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {inventorySummary.map(({ label, value, detail, Icon, color }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={19} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 truncate text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('equipment.searchPlaceholder')}
                    className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 py-3 pl-11 pr-4 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  />
                </label>
                <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0">
                  <button
                    type="button"
                    onClick={() => setActiveCategory('all')}
                    className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold transition ${
                      activeCategory === 'all'
                        ? 'bg-zinc-950 text-white'
                        : 'border border-zinc-200 bg-white text-zinc-600 hover:border-lime-300 hover:text-zinc-950'
                    }`}
                  >
                    {t('equipment.all')}
                  </button>
                  {activeCategoryOptions.map((category) => {
                    const meta = getCategoryMeta(category);
                    return (
                      <button
                        type="button"
                        key={category}
                        onClick={() => setActiveCategory(category)}
                        className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold transition ${
                          activeCategory === category
                            ? 'bg-zinc-950 text-white'
                            : 'border border-zinc-200 bg-white text-zinc-600 hover:border-lime-300 hover:text-zinc-950'
                        }`}
                      >
                        {t(meta.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <div className="h-4 w-28 animate-pulse rounded bg-zinc-200" />
                    <div className="mt-5 h-24 animate-pulse rounded-xl bg-zinc-100" />
                    <div className="mt-4 h-3 w-2/3 animate-pulse rounded bg-zinc-100" />
                  </div>
                ))}
              </div>
            )}

            {!isLoading && loadError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
                <AlertTriangle className="mx-auto text-rose-600" size={34} />
                <h2 className="mt-4 text-xl font-semibold text-rose-950">{t('equipment.loadErrorTitle')}</h2>
                <p className="mt-2 text-sm text-rose-700">{loadError}</p>
                <button
                  type="button"
                  onClick={fetchEquipment}
                  className="mt-5 inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
                >
                  {t('common.retry')}
                </button>
              </div>
            )}

            {!isLoading && !loadError && equipmentList.length === 0 && (
              <div className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
                  <Package size={30} />
                </div>
                <h2 className="mt-5 text-2xl font-semibold tracking-tight text-zinc-950">{t('equipment.emptyTitle')}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
                  {t('equipment.emptyBody')}
                </p>
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-lime-300 px-5 text-sm font-semibold text-zinc-950"
                >
                  <Plus size={18} />
                  {t('equipment.addItem')}
                </button>
              </div>
            )}

            {!isLoading && !loadError && equipmentList.length > 0 && filteredEquipment.length === 0 && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
                <Search className="mx-auto text-zinc-400" size={34} />
                <h2 className="mt-4 text-xl font-semibold text-zinc-950">{t('equipment.noMatchingTitle')}</h2>
                <p className="mt-2 text-sm text-zinc-500">{t('equipment.noMatchingBody')}</p>
              </div>
            )}

            {!isLoading && !loadError && filteredEquipment.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {filteredEquipment.map(renderEquipmentCard)}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid="equipment-usage-history">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('equipment.usageHistory')}</h2>
                  <p className="text-sm text-zinc-500">{t('equipment.usageTotal')}</p>
                </div>
                <Wrench className="text-lime-700" size={21} />
              </div>

              {equipmentList.length > 0 ? (
                <div className="space-y-4">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-zinc-700">{t('equipment.inventoryDetail')}</span>
                    <select
                      className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                      value={selectedEquipmentId || ''}
                      onChange={(event) => setSelectedEquipmentId(event.target.value)}
                    >
                      {equipmentList.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  {selectedEquipment ? (
                    <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-zinc-950">{selectedEquipment.name}</p>
                          <p className="mt-1 text-xs text-zinc-500">{getCategoryLabel(selectedEquipment.category)}</p>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${getConditionMeta(selectedEquipment).tone}`}>
                          {t(getConditionMeta(selectedEquipment).labelKey)}
                        </span>
                      </div>
                      <div className="mt-4 flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm">
                        <span className="font-medium text-zinc-500">{t('equipment.usageTotal')}</span>
                        <span className="font-semibold text-zinc-950">{formatUsageDuration(usageTotalMinutes)}</span>
                      </div>
                      {selectedEquipment.need_to_buy ? (
                        <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800">
                          {t('equipment.replacementRecommended')}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {usageError ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      {usageError}
                    </div>
                  ) : null}

                  {usageLoading ? (
                    <div className="space-y-3">
                      {[0, 1].map((item) => (
                        <div key={item} className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                          <div className="h-3 w-28 animate-pulse rounded bg-zinc-200" />
                          <div className="mt-3 h-3 w-40 animate-pulse rounded bg-zinc-100" />
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {!usageLoading && !usageError && usageLogs.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-600">
                      <p className="font-semibold text-zinc-950">{t('equipment.noUsageHistoryYet')}</p>
                      <p className="mt-1">{t('equipment.usageEmptyBody')}</p>
                    </div>
                  ) : null}

                  {!usageLoading && !usageError && usageLogs.length > 0 ? (
                    <div className="space-y-3">
                      {usageLogs.map((log) => (
                        <div key={log.id || `${log.source_type}-${log.source_id}-${log.date}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-zinc-950">
                                {usageSourceLabel(log.source_type)}
                              </p>
                              <p className="mt-1 text-xs text-zinc-500">
                                {localeDateString(log.date)}
                              </p>
                            </div>
                            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-zinc-700 ring-1 ring-zinc-200">
                              {formatUsageDuration(log.duration_minutes)}
                            </span>
                          </div>
                          <div className="mt-3 grid gap-2 text-xs text-zinc-500">
                            <div className="flex items-center justify-between gap-3">
                              <span>{t('equipment.source')}</span>
                              <span className="truncate font-medium text-zinc-800">{log.source_id || '-'}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span>{t('equipment.duration')}</span>
                              <span className="font-medium text-zinc-800">{formatUsageDuration(log.duration_minutes)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm text-zinc-500">
                  {t('equipment.noEquipmentAddedYet')}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid="shopping-list">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('equipment.replacementReminders')}</h2>
                  <p className="text-sm text-zinc-500">{t('equipment.replacementRemindersBody')}</p>
                </div>
                <AlertTriangle className={attentionItems.length ? 'text-rose-600' : 'text-emerald-600'} size={21} />
              </div>

              {attentionItems.length > 0 ? (
                <div className="space-y-3">
                  {attentionItems.map((item) => {
                    const meta = getCategoryMeta(item.category);
                    return (
                      <div key={item.id} className="rounded-xl border border-rose-100 bg-rose-50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-rose-950">{item.name}</p>
                            <p className="mt-1 text-sm text-rose-700">{t(meta.labelKey)}</p>
                          </div>
                          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-rose-700">
                            {t('equipment.review')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800">
                  {t('equipment.noReplacementItems')}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-950 text-lime-300">
                  <Wrench size={19} />
                </div>
                <div>
                  <h2 className="font-semibold tracking-tight text-zinc-950">{t('equipment.maintenanceNotes')}</h2>
                  <p className="text-sm text-zinc-500">{t('equipment.maintenanceNotesBody')}</p>
                </div>
              </div>
              <div className="space-y-3">
                {activeCategoryOptions.slice(0, 5).map((category) => {
                  const meta = getCategoryMeta(category);
                  return (
                    <div key={category} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-3">
                      <span className="flex items-center gap-2 text-sm font-medium text-zinc-700">
                        <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                        {t(meta.labelKey)}
                      </span>
                      <span className="text-sm font-semibold text-zinc-950">{categoryCounts[category]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>
        </section>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 p-4" data-testid="equipment-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-lime-700">{t('equipment.inventoryDetail')}</p>
                <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">
                  {editingId ? t('equipment.editItem') : t('equipment.addItem')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:bg-zinc-50"
                aria-label={t('equipment.closeFormAria')}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('equipment.nameLabel')}</label>
                <input
                  type="text"
                  className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="input-name"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('equipment.category')}</label>
                  <select
                    className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="racket">{t('equipment.categories.racket')}</option>
                    <option value="strings">{t('equipment.categories.strings')}</option>
                    <option value="shoes">{t('equipment.categories.shoes')}</option>
                    <option value="clothing">{t('equipment.categories.clothing')}</option>
                    <option value="balls">{t('equipment.categories.balls')}</option>
                    <option value="bag">{t('equipment.categories.bag')}</option>
                    <option value="other">{t('equipment.categories.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('equipment.condition')}</label>
                  <select
                    className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                  >
                    <option value="excellent">{t('equipment.conditions.excellent')}</option>
                    <option value="good">{t('equipment.conditions.good')}</option>
                    <option value="fair">{t('equipment.conditions.fair')}</option>
                    <option value="poor">{t('equipment.conditions.poor')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('equipment.purchaseDate')}</label>
                <input
                  type="date"
                  className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('common.notes')}</label>
                <textarea
                  className="min-h-28 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('equipment.notesPlaceholder')}
                />
              </div>

              <label className="flex min-h-14 cursor-pointer items-center justify-between gap-4 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                <span>
                  <span className="block text-sm font-semibold text-zinc-950">{t('equipment.markForReplacement')}</span>
                  <span className="block text-xs text-zinc-500">{t('equipment.markForReplacementBody')}</span>
                </span>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-lime-500"
                  checked={formData.need_to_buy}
                  onChange={(e) => setFormData({ ...formData, need_to_buy: e.target.checked })}
                />
              </label>

              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingId(null);
                    resetForm();
                  }}
                  className="inline-flex min-h-11 flex-1 items-center justify-center rounded-full border border-zinc-200 px-5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                  data-testid="submit-equipment"
                >
                  <Plus size={18} />
                  {editingId ? t('equipment.saveChanges') : t('equipment.addItem')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
